// ─────────────────────────────────────────────
// src/pages/FairfaxPage.jsx
// Local landing page for Fairfax, VA / Fairfax County search intent
// ("STEM programs for kids in Fairfax VA").
// ─────────────────────────────────────────────
import { useEffect } from "react";
import { StarField, GlassCard, Btn, SectionLabel } from "../components/UI";
import Footer from "../components/Footer";

export default function FairfaxPage({ setPage }) {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = '__fairfax-schema';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "STEM Programs for Kids in Fairfax, VA",
      "url": "https://www.marsacademy.ai/fairfax-va",
      "description": "Mars Academy partners with schools across Fairfax County, Virginia to bring hands-on STEM, coding, and technology programs to elementary and middle school students.",
      "about": {
        "@type": "EducationalOrganization",
        "@id": "https://www.marsacademy.ai/#organization"
      }
    });
    document.head.appendChild(script);
    return () => document.getElementById('__fairfax-schema')?.remove();
  }, []);

  const reasons = [
    { icon: "🏫", title: "In-Person, at School",         desc: "We work directly with Fairfax County schools and PTAs to run classes on-site, before or after the school day." },
    { icon: "🏅", title: "IDCA-Certified Curriculum",     desc: "Our coding, AI, and digital infrastructure curriculum is reviewed and certified by the International Data Center Authority." },
    { icon: "👨‍👩‍👧", title: "Built for Local Families",       desc: "Founded and run out of Fairfax, Virginia, with a program designed around the schedules of Northern Virginia school communities." },
  ];

  return (
    <div>
      {/* Header */}
      <section style={{ padding:"8rem 2rem 4rem", textAlign:"center", position:"relative", overflow:"hidden" }}>
        <StarField count={40} />
        <div style={{ position:"relative", zIndex:2, maxWidth:760, margin:"0 auto" }}>
          <SectionLabel color="#FFD93D">Fairfax, Virginia</SectionLabel>
          <h1 style={{ fontFamily:"'Fredoka One',cursive", fontSize:"clamp(2rem,5vw,3.4rem)", lineHeight:1.2, marginBottom:"1rem" }}>
            STEM Programs for Kids in Fairfax, VA
          </h1>
          <p style={{ color:"rgba(255,255,255,0.55)", fontSize:"1rem", fontWeight:600, lineHeight:1.8, maxWidth:640, margin:"0 auto" }}>
            Mars Academy is based in Fairfax, Virginia and partners with elementary and middle schools across Fairfax County and the surrounding Northern Virginia area to teach kids coding, AI, and technology, right at their own school.
          </p>
        </div>
      </section>

      {/* Local presence */}
      <section style={{ padding:"1rem 2rem 3rem", maxWidth:1100, margin:"0 auto" }}>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))", gap:"1.2rem" }}>
          {reasons.map((r, i) => (
            <GlassCard key={i} style={{ padding:"1.6rem" }}>
              <div style={{ fontSize:"1.9rem", marginBottom:"0.6rem" }}>{r.icon}</div>
              <h2 style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.05rem", marginBottom:"0.5rem" }}>{r.title}</h2>
              <p style={{ color:"rgba(255,255,255,0.5)", fontSize:"0.86rem", lineHeight:1.65, fontWeight:600 }}>{r.desc}</p>
            </GlassCard>
          ))}
        </div>
      </section>

      {/* What we teach */}
      <section style={{ padding:"1rem 2rem 3rem", maxWidth:900, margin:"0 auto" }}>
        <GlassCard style={{ padding:"2rem 2.2rem" }}>
          <h2 style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.3rem", marginBottom:"0.8rem", color:"#FFD93D" }}>Serving Fairfax County Schools and Families</h2>
          <p style={{ color:"rgba(255,255,255,0.6)", fontSize:"0.95rem", lineHeight:1.85, fontWeight:600, marginBottom:"1rem" }}>
            Parents and schools across Fairfax County know their students spend hours a day around technology, often without understanding how any of it actually works. Mars Academy's before- and after-school program closes that gap with hands-on lessons in computer basics, internet safety, coding with Scratch, artificial intelligence, cloud computing, and digital infrastructure, all taught in person at your child's school.
          </p>
          <p style={{ color:"rgba(255,255,255,0.6)", fontSize:"0.95rem", lineHeight:1.85, fontWeight:600 }}>
            We're always working to bring the program to more schools and communities throughout Northern Virginia. If your school's PTA or community organization is interested in hosting Mars Academy, we'd love to hear from you.
          </p>
        </GlassCard>
      </section>

      {/* CTA */}
      <section style={{ padding:"1rem 2rem 5rem", maxWidth:780, margin:"0 auto" }}>
        <div style={{ background:"linear-gradient(135deg,rgba(255,107,107,0.12),rgba(155,92,246,0.12))", border:"1.5px solid rgba(255,255,255,0.1)", borderRadius:28, padding:"2.6rem 2rem", textAlign:"center" }}>
          <h2 style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.4rem", marginBottom:"0.8rem" }}>Bring Mars Academy to Your School</h2>
          <p style={{ color:"rgba(255,255,255,0.6)", fontSize:"0.94rem", fontWeight:600, lineHeight:1.8, maxWidth:560, margin:"0 auto 1.6rem" }}>
            Learn more about how our before- and after-school program runs day to day, or reach out about scheduling and registration for a Fairfax County school.
          </p>
          <div style={{ display:"flex", gap:"0.9rem", justifyContent:"center", flexWrap:"wrap" }}>
            <Btn onClick={() => setPage("after-school-programs")} variant="primary">See Our Programs</Btn>
            <Btn onClick={() => setPage("faq")} variant="secondary">Read the FAQ</Btn>
          </div>
        </div>
      </section>

      <Footer setPage={setPage} />
    </div>
  );
}
